import { TextInfoGroupAlt } from "@/components/shared";
import { CardDescription } from "@/components/ui/card";
import { useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import { convertBpToDecimal, formatDateLocale, formatNumberLocale } from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { useOutletContext, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import {
  DecisionEnum,
  FetchPriceDetailsDocument,
  FetchPriceDetailsQuery,
  FetchPriceDetailsQueryVariables,
  Service,
  ServiceType,
  StandalonePriceDetails,
  Tender,
  TenderPriceDetails,
} from "@/graphql/__generated__/graphql";
import { Skeleton } from "@/components/ui/skeleton";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useQuery } from "@apollo/client/react";
import { CalculationOverviewSubject, getCalculationOverviewSubjectKey } from "../types";
import SelectableCard from "./SelectableCard";
import { getTenderKpiItems } from "./helpers";
import ServiceListFilters from "./ServiceListFilters";

interface ServiceListProps {
  initialOrganizationId?: string;
  defaultOrganizationId?: string;
  initialServiceId?: number;
  setSelectedSubject: Dispatch<SetStateAction<CalculationOverviewSubject | null>>;
  selectedSubject: CalculationOverviewSubject | null;
  setSelectedComparingSubject: Dispatch<SetStateAction<CalculationOverviewSubject | null>>;
  selectedComparingSubject: CalculationOverviewSubject | null;
  tenderData?: Tender;
  /** Callback to notify parent when standalone data is loaded */
  onStandaloneDataLoaded?: (_data: { standaloneCars?: StandalonePriceDetails; standalonePurchases?: StandalonePriceDetails }) => void;
}

/**
 * ServiceList - Renders the list of tender/services to select (and optionally compare) in Calculation Overview.
 * Includes the filter controls and the scrollable list of selectable cards.
 *
 * @component
 * @example
 * <ServiceList
 *   initialOrganizationId={initialOrganizationId}
 *   defaultOrganizationId={defaultOrganizationId}
 *   setSelectedSubject={setSelectedSubject}
 *   selectedSubject={selectedSubject}
 *   setSelectedComparingSubject={setSelectedComparingSubject}
 *   selectedComparingSubject={selectedComparingSubject}
 * />
 */
function ServiceList({
  initialOrganizationId,
  defaultOrganizationId,
  initialServiceId,
  setSelectedSubject,
  selectedSubject,
  setSelectedComparingSubject,
  selectedComparingSubject,
  tenderData,
  onStandaloneDataLoaded,
}: ServiceListProps) {
  const { t } = useTranslation();
  const { tenderID } = useParams();
  const intl = useIntl();
  const [selectedOrganization, setSelectedOrganization] = useState<string>(initialOrganizationId ?? "all");
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState<"all" | "permanent" | "mobile">("all");

  const outletContext = useOutletContext<EditTenderContext | undefined>();
  const tenderFromContext = outletContext?.tenderByIdData?.fetchTenderById;
  const tender = tenderData ?? tenderFromContext;

  const bidDecisionGroups = tender?.bidDecisionGroups ?? [];
  const contract = tender?.contract;

  const biddingOrganizations = [
    { id: "all", name: t("edittender.calculationoverview.common.allorganizations") },
    ...bidDecisionGroups
      .filter((group) => group.currentDecision === DecisionEnum.Bid)
      .map((group) => ({
        id: group.operationalOrganizationId.toString(),
        name: group.operationalOrganizationShortName
      }))
  ];

  const { data: servicesData, loading: servicesLoading } = useFetchServicesOnTenderQueryHook(Number(tenderID), true);
  const { data: tenderPriceData, loading: tenderPriceLoading } = useQuery<FetchPriceDetailsQuery, FetchPriceDetailsQueryVariables>(
    FetchPriceDetailsDocument,
    {
      variables: {
        tenderId: Number(tenderID),
        includeTenderPriceDetails: true,
        includePricingPerService: false,
        includeStandaloneCars: true,
        includeStandalonePurchases: true,
      },
      fetchPolicy: "cache-and-network"
    }
  );

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const services = servicesData?.fetchServicesOnTender?.services ?? [];
  const filteredServices = services.filter((service) => {
    const matchesOrganization =
      selectedOrganization === "all" || service.operationalOrganizationId.toString() === selectedOrganization;

    const matchesType =
      serviceTypeFilter === "all" ||
      (serviceTypeFilter === "permanent" && service.type === ServiceType.Permanent) ||
      (serviceTypeFilter === "mobile" && service.type === ServiceType.Mobile);

    const matchesSearch =
      normalizedSearchTerm.length === 0 || (service.name ?? "").toLowerCase().includes(normalizedSearchTerm);

    return matchesOrganization && matchesType && matchesSearch;
  });

  const tenderCostDetails = servicesData?.fetchServicesOnTender?.tenderCostDetails;
  const tenderPriceDetails = tenderPriceData?.fetchPriceDetails?.tenderPriceDetails;
  const standaloneCars = tenderPriceData?.fetchPriceDetails?.standaloneCars;
  const standalonePurchases = tenderPriceData?.fetchPriceDetails?.standalonePurchases;

  // Notify parent when standalone data is available
  useEffect(() => {
    if (onStandaloneDataLoaded && (standaloneCars || standalonePurchases)) {
      onStandaloneDataLoaded({
        standaloneCars: standaloneCars as StandalonePriceDetails | undefined,
        standalonePurchases: standalonePurchases as StandalonePriceDetails | undefined,
      });
    }
  }, [standaloneCars, standalonePurchases, onStandaloneDataLoaded]);

  // Memoize tenderSubject to prevent unnecessary re-renders and infinite loops
  const tenderSubject: CalculationOverviewSubject | null = useMemo(() => {
    if (!tenderPriceDetails) return null;
    return {
      kind: "tender",
      tender: tenderPriceDetails as TenderPriceDetails,
      tenderCostDetails: tenderCostDetails ?? [],
      standaloneCars: standaloneCars ?? undefined,
      standalonePurchases: standalonePurchases ?? undefined,
    };
  }, [tenderPriceDetails, tenderCostDetails, standaloneCars, standalonePurchases]);

  const appliedOrganizationIdRef = useRef<string | null>(null);
  const previousTenderDataRef = useRef<string | null>(null);

  // Ensure organization filter applies once if it arrives after mount.
  useEffect(() => {
    if (!initialOrganizationId || selectedOrganization !== "all") {
      return;
    }

    if (appliedOrganizationIdRef.current === initialOrganizationId) {
      return;
    }

    appliedOrganizationIdRef.current = initialOrganizationId;
    setSelectedOrganization(initialOrganizationId);
  }, [initialOrganizationId, selectedOrganization]);

  // Create a stable reference key for tender data to detect actual changes
  const tenderDataKey = useMemo(() => {
    if (!tenderPriceDetails) return null;
    // Create a key based on the tender ID, key pricing values, and cost details length
    // This ensures the subject is updated when cost details load after price details
    const costDetailsLength = tenderCostDetails?.length ?? 0;
    return `${tenderPriceDetails.id}-${tenderPriceDetails.revenue}-${tenderPriceDetails.margin}-${tenderPriceDetails.pricePerHour}-${costDetailsLength}`;
  }, [tenderPriceDetails, tenderCostDetails]);

  // Auto-select tender as default when it becomes available and no subject is selected
  // OR auto-select the specified service if initialServiceId is provided
  // Also update selectedSubject when tenderSubject changes (e.g., after services are deleted)
  useEffect(() => {
    if (initialServiceId && services.length > 0 && selectedSubject === null) {
      const serviceToSelect = services.find((service) => service.id === initialServiceId);
      if (serviceToSelect) {
        const serviceSubject: CalculationOverviewSubject = { kind: "service", service: serviceToSelect as Service };
        setSelectedSubject(serviceSubject);
        return;
      }
    }
    if (tenderSubject && tenderDataKey) {
      // If no subject is selected, select the tender subject
      if (selectedSubject === null && !initialServiceId) {
        setSelectedSubject(tenderSubject);
        previousTenderDataRef.current = tenderDataKey;
      }
      // If the currently selected subject is a tender subject, update it only if the data actually changed
      else if (selectedSubject?.kind === "tender" && previousTenderDataRef.current !== tenderDataKey) {
        setSelectedSubject(tenderSubject);
        previousTenderDataRef.current = tenderDataKey;
      }
    } else if (!tenderSubject) {
      previousTenderDataRef.current = null;
    }
  }, [tenderSubject, tenderDataKey, selectedSubject, setSelectedSubject, initialServiceId, services]);

  const selectedKey = selectedSubject ? getCalculationOverviewSubjectKey(selectedSubject) : null;
  const comparingKey = selectedComparingSubject ? getCalculationOverviewSubjectKey(selectedComparingSubject) : null;
  const tenderKey = tenderSubject ? getCalculationOverviewSubjectKey(tenderSubject) : null;

  const onCardClickHandler = (subject: CalculationOverviewSubject) => {
    if (comparingKey !== getCalculationOverviewSubjectKey(subject)) {
      setSelectedSubject(subject);
    }
  };

  const onCompareButtonClickHandler = (e: React.MouseEvent, subject: CalculationOverviewSubject) => {
    e.stopPropagation();
    const key = getCalculationOverviewSubjectKey(subject);
    setSelectedComparingSubject(comparingKey === key ? null : subject);
  };

  const hasPrimarySelection = selectedSubject !== null;
  const tenderKpiItems = getTenderKpiItems({ t, intl, tenderPriceDetails });

  return (
    <div className="flex flex-col gap-2">
      {/* Header */}
      <div className="space-y-2">
        <CardDescription className="flex items-center">{t("edittender.calculationoverview.description")}</CardDescription>
        <ServiceListFilters
          defaultOrganizationId={defaultOrganizationId}
          selectedOrganization={selectedOrganization}
          onSelectedOrganizationChange={setSelectedOrganization}
          organizations={biddingOrganizations}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          serviceTypeFilter={serviceTypeFilter}
          onServiceTypeFilterChange={setServiceTypeFilter}
        />
      </div>
      <Separator className="mt-2" />

      {/* List of services */}
      <ScrollArea className="h-[calc(100vh-250px)] pr-4">
        {
          servicesLoading || tenderPriceLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="w-full h-20" />
              <Skeleton className="w-full h-20" />
              <Skeleton className="w-full h-20" />
              <Skeleton className="w-full h-20" />
              <Skeleton className="w-full h-20" />
            </div>
          ) : (
            <div className="flex flex-col gap-2 pb-[300px]">
              {tenderSubject && (
                <SelectableCard
                  title={tenderPriceDetails?.name}
                  subtitle={
                    <>
                      <span className="mr-2">{t("edittender.calculationoverview.common.contractperiod")}</span>
                      <span className="font-medium text-primary">{`${formatDateLocale(intl, contract?.startDate ?? "")} - ${formatDateLocale(intl, contract?.endDate ?? "")}`}</span>
                    </>
                  }
                  badge={
                    <Badge variant="default" className="text-xs">
                      {t("common:chance")}
                    </Badge>
                  }
                  isSelected={selectedKey === tenderKey}
                  isComparing={comparingKey === tenderKey}
                  hasPrimarySelection={hasPrimarySelection}
                  onSelect={() => onCardClickHandler(tenderSubject)}
                  onToggleCompare={(e) => onCompareButtonClickHandler(e, tenderSubject)}
                  className="border-primary"
                >
                  <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                    {tenderKpiItems.map((item) => (
                      <TextInfoGroupAlt
                        key={`tender-kpi-${item.key}`}
                        label={item.label}
                        content={item.content}
                        hasBackground={true}
                        backgroundColor="slate"
                        tooltip={item.tooltip}
                      />
                    ))}
                  </div>
                </SelectableCard>
              )}
              <Separator className="my-2" />
              {
                filteredServices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-4">
                    <p className="text-muted-foreground text-center text-sm">
                      {t("edittender.calculationoverview.common.noservices")}
                    </p>
                  </div>
                ) : (
                  filteredServices.map((service) => {
                    const serviceSubject: CalculationOverviewSubject = { kind: "service", service: service as Service };
                    const serviceKey = getCalculationOverviewSubjectKey(serviceSubject);

                    const serviceKpiItems = [
                      {
                        key: "margin",
                        label: t("common:pricing.labels.totalmargin"),
                        content: `${convertBpToDecimal(service.margin)}%`,
                        tooltip: t("common:pricing.tooltips.totalmargin")
                      },
                      {
                        key: "totalSurcharge",
                        label: t("common:pricing.labels.totalsurcharge"),
                        content: `${convertBpToDecimal(service.pricing?.totalSurcharge ?? 0)}%`,
                        tooltip: t("common:pricing.tooltips.totalsurcharge"),
                      },
                      {
                        key: "revenue",
                        label: t("common:pricing.labels.totalrevenue"),
                        content: formatNumberLocale(intl, service.pricing?.totalRevenue ?? 0, "currency"),
                        tooltip: t("common:pricing.tooltips.totalrevenue")
                      },
                      {
                        key: "price",
                        label: t("common:pricing.labels.totalpriceperhour"),
                        content: formatNumberLocale(intl, service.pricing?.totalPricePerHour ?? 0, "currency"),
                        tooltip: t("common:pricing.tooltips.totalpriceperhour")
                      },
                      {
                        key: "wage",
                        label: t("edittender.calculationoverview.labels.wage.label"),
                        tooltip: t("edittender.calculationoverview.labels.wage.tooltip"),
                        content: `${formatNumberLocale(intl, service.hourlyWage, "currency")} + ${service.nonTariffAllowance > 0 ?
                          formatNumberLocale(intl, service.nonTariffAllowance, "currency") : ""}`,
                      },
                      {
                        key: "totalEmployeeCount",
                        label: t("common:pricing.labels.totalemployeecount"),
                        content: formatNumberLocale(intl, service.pricing?.totalEmployeeCount ?? 0, "decimal")
                      },
                    ].filter((item) => item.content !== "");

                    return (
                      <SelectableCard
                        key={service.id}
                        title={
                          <span className="break-all">
                            {service.name}
                            <span className="text-sm text-slate-500 font-normal ml-2">{service.operationalOrganizationShortName}</span>
                          </span>
                        }
                        badge={<Badge variant="outline" className="text-xs">{t(`common:servicetype.${service.type.toLowerCase()}`)}</Badge>}
                        isSelected={selectedKey === serviceKey}
                        isComparing={comparingKey === serviceKey}
                        hasPrimarySelection={hasPrimarySelection}
                        onSelect={() => onCardClickHandler(serviceSubject)}
                        onToggleCompare={(e) => onCompareButtonClickHandler(e, serviceSubject)}
                      >
                        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                          {serviceKpiItems.map((item) => (
                            <TextInfoGroupAlt
                              key={`service-kpi-${service.id}-${item.key}`}
                              label={item.label}
                              content={item.content}
                              hasBackground={true}
                              backgroundColor="slate"
                              tooltip={item.tooltip}
                            />
                          ))}
                        </div>
                      </SelectableCard>
                    );
                  })
                )
              }
            </div>
          )
        }
      </ScrollArea>
    </div>
  );
}

export default ServiceList;
